import { useState, useEffect, useMemo, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Truck, Search, Calendar, User, Package, CheckCircle, Clock, 
  FileText, MapPin, Phone, Loader2, Filter, Eye, Check, X,
  AlertTriangle, Printer, Upload, Image, File, Trash2, ClipboardList, UserCheck
} from 'lucide-react';
import { formatPhone, formatDateDisplay } from '@/utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Entregas() {
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pendentes');
  const [selectedEntrega, setSelectedEntrega] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [viewingEntrega, setViewingEntrega] = useState(null);
  
  // Estado para upload de arquivos na conclusão
  const [arquivos, setArquivos] = useState([]);
  const [observacoesConclusao, setObservacoesConclusao] = useState('');
  const fileInputRef = useRef(null);
  
  // Estados para entregas pendentes de OS
  const [tabAtiva, setTabAtiva] = useState('entregas');
  const [entregasPendentesOS, setEntregasPendentesOS] = useState([]);
  const [loadingPendentes, setLoadingPendentes] = useState(false);
  const [selectedPendente, setSelectedPendente] = useState(null);
  const [itensSelecionadosEntrega, setItensSelecionadosEntrega] = useState([]);
  const [gerandoEntrega, setGerandoEntrega] = useState(false);
  const [entregadorNome, setEntregadorNome] = useState('');
  const [filterStatusOS, setFilterStatusOS] = useState('pendentes'); // Filtro para aba Pendentes OS
  const [isRetirada, setIsRetirada] = useState(false); // Se é retirada pelo cliente
  const [confirmandoRetirada, setConfirmandoRetirada] = useState(null); // Modal de confirmar retirada

  // Carregar todas as entregas
  const fetchEntregas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/entregas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntregas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar entregas:', error);
      toast.error('Erro ao carregar entregas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntregas();
    fetchEntregasPendentesOS();
  }, []);

  // Buscar entregas pendentes de OS
  const fetchEntregasPendentesOS = async () => {
    try {
      setLoadingPendentes(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/entregas-pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntregasPendentesOS(response.data.entregas || []);
    } catch (error) {
      console.error('Erro ao carregar entregas pendentes:', error);
    } finally {
      setLoadingPendentes(false);
    }
  };

  // Abrir modal para gerar entrega
  const abrirGerarEntrega = (pendente) => {
    setSelectedPendente(pendente);
    setEntregadorNome('');
    // Pré-selecionar todos os itens pendentes
    const itens = pendente.itens?.filter(i => i.status !== 'entregue').map(i => ({
      ...i,
      quantidade_a_entregar: i.quantidade_pedida - (i.quantidade_entregue || 0)
    })) || [];
    setItensSelecionadosEntrega(itens);
  };

  // Gerar entrega a partir de pendente
  const handleGerarEntrega = async () => {
    if (!selectedPendente || itensSelecionadosEntrega.length === 0) {
      toast.error('Selecione pelo menos um item');
      return;
    }
    
    // Validar entregador apenas se NÃO for retirada
    if (!isRetirada && !entregadorNome.trim()) {
      toast.error('Informe o nome do entregador');
      return;
    }
    
    try {
      setGerandoEntrega(true);
      const token = localStorage.getItem('token');
      
      const itensParaEnviar = itensSelecionadosEntrega
        .filter(i => i.quantidade_a_entregar > 0)
        .map(i => ({
          produto_id: i.produto_id,
          nome: i.nome,
          quantidade: i.quantidade_a_entregar
        }));
      
      await axios.post(
        `${API}/entregas-pendentes/${selectedPendente.id}/gerar-entrega`,
        { 
          itens: itensParaEnviar, 
          entregador: isRetirada ? 'RETIRADA PELO CLIENTE' : entregadorNome.trim(),
          tipo_entrega: isRetirada ? 'retirada' : 'entrega'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isRetirada ? 'Retirada registrada com sucesso!' : 'Entrega gerada com sucesso!');
      setSelectedPendente(null);
      setItensSelecionadosEntrega([]);
      setEntregadorNome('');
      setIsRetirada(false);
      // Mudar para aba de Entregas e mostrar Pendentes para ver a nova entrega
      setTabAtiva('entregas');
      setFilterStatus('pendentes');
      fetchEntregas();
      fetchEntregasPendentesOS();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar entrega');
    } finally {
      setGerandoEntrega(false);
    }
  };

  // Confirmar que cliente retirou (marca como concluída direto)
  const handleConfirmarRetirada = async () => {
    if (!confirmandoRetirada) return;
    
    try {
      setConfirmando(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/entregas/${confirmandoRetirada.id}/concluir`,
        { 
          observacoes: 'Cliente retirou os produtos no local.',
          anexos: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Retirada confirmada com sucesso!');
      setConfirmandoRetirada(null);
      fetchEntregas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar retirada');
    } finally {
      setConfirmando(false);
    }
  };

  // Abrir PDF do romaneio
  const abrirPDF = async (entrega) => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/entregas/${entrega.id}/romaneio-pdf?token=${token}`, '_blank');
    } catch (error) {
      toast.error('Erro ao abrir romaneio');
    }
  };

  // Atualizar quantidade a entregar
  const updateQuantidadeEntrega = (index, quantidade) => {
    const newItens = [...itensSelecionadosEntrega];
    const maxQtd = newItens[index].quantidade_pedida - (newItens[index].quantidade_entregue || 0);
    newItens[index].quantidade_a_entregar = Math.min(Math.max(0, quantidade), maxQtd);
    setItensSelecionadosEntrega(newItens);
  };

  // Excluir entrega pendente (para OS de estoque interno)
  const excluirEntregaPendente = async (pendenteId) => {
    if (!window.confirm('Excluir esta entrega pendente? (Use para remover OSs de estoque interno)')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/entregas-pendentes/${pendenteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entrega pendente excluída');
      fetchEntregasPendentesOS();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Filtrar entregas
  const entregasFiltradas = useMemo(() => {
    return entregas.filter(entrega => {
      if (filterStatus === 'pendentes' && entrega.status === 'Concluída') return false;
      if (filterStatus === 'concluidas' && entrega.status !== 'Concluída') return false;
      
      if (searchTerm) {
        const termo = searchTerm.toLowerCase();
        const matchCliente = entrega.cliente_nome?.toLowerCase().includes(termo);
        const matchPedido = entrega.numero_pedido?.toLowerCase().includes(termo);
        const matchRomaneio = String(entrega.numero_romaneio).includes(termo);
        const matchEntregador = entrega.entregador?.toLowerCase().includes(termo);
        if (!matchCliente && !matchPedido && !matchRomaneio && !matchEntregador) return false;
      }
      
      return true;
    });
  }, [entregas, filterStatus, searchTerm]);

  // Estatísticas
  const stats = useMemo(() => {
    const pendentes = entregas.filter(e => e.status !== 'Concluída').length;
    const concluidas = entregas.filter(e => e.status === 'Concluída').length;
    const hoje = entregas.filter(e => {
      const dataEntrega = new Date(e.data_entrega).toDateString();
      const dataHoje = new Date().toDateString();
      return dataEntrega === dataHoje && e.status !== 'Concluída';
    }).length;
    return { pendentes, concluidas, hoje, total: entregas.length };
  }, [entregas]);

  // Filtro para entregas pendentes de OS
  const entregasPendentesOSFiltradas = useMemo(() => {
    return entregasPendentesOS.filter(pendente => {
      const isEntregue = pendente.status === 'entregue';
      if (filterStatusOS === 'pendentes' && isEntregue) return false;
      if (filterStatusOS === 'concluidas' && !isEntregue) return false;
      return true;
    });
  }, [entregasPendentesOS, filterStatusOS]);

  // Contador para badge da aba Pendentes OS (apenas pendentes)
  const countPendentesOS = useMemo(() => {
    return entregasPendentesOS.filter(p => p.status !== 'entregue').length;
  }, [entregasPendentesOS]);

  // Abrir modal de conclusão
  const abrirConclusao = (entrega) => {
    setSelectedEntrega(entrega);
    setArquivos([]);
    setObservacoesConclusao('');
  };

  // Adicionar arquivos
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setArquivos(prev => [...prev, ...files]);
  };

  // Remover arquivo
  const removerArquivo = (index) => {
    setArquivos(prev => prev.filter((_, i) => i !== index));
  };

  // Concluir entrega com anexos
  const concluirEntrega = async () => {
    if (!selectedEntrega) return;
    
    setConfirmando(true);
    try {
      const token = localStorage.getItem('token');
      
      // Se tem arquivos, usar o endpoint com anexos
      if (arquivos.length > 0 || observacoesConclusao) {
        const formData = new FormData();
        arquivos.forEach(file => {
          formData.append('files', file);
        });
        formData.append('observacoes_conclusao', observacoesConclusao);
        
        await axios.post(`${API}/entregas/${selectedEntrega.id}/concluir-com-anexos`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Sem anexos, usar endpoint simples
        await axios.post(`${API}/entregas/${selectedEntrega.id}/concluir`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`Entrega do Romaneio #${String(selectedEntrega.numero_romaneio).padStart(4, '0')} concluída!`);
      setSelectedEntrega(null);
      setArquivos([]);
      setObservacoesConclusao('');
      fetchEntregas();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erro ao concluir entrega';
      toast.error(message);
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-manrope flex items-center gap-3">
              <Truck className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
              Central de Entregas
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie todas as entregas e romaneios
            </p>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Pendentes</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.pendentes}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Para Hoje</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.hoje}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500/30" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Concluídas</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.concluidas}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Package className="h-8 w-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Principais */}
        <Tabs value={tabAtiva} onValueChange={setTabAtiva} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="entregas" className="gap-2">
              <Truck className="h-4 w-4" />
              Entregas
            </TabsTrigger>
            <TabsTrigger value="pendentes-os" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              Pendentes (OS)
              {countPendentesOS > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {countPendentesOS}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab de Entregas */}
          <TabsContent value="entregas" className="space-y-4">
            {/* Filtros */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por cliente, pedido, romaneio ou entregador..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Tabs value={filterStatus} onValueChange={setFilterStatus} className="w-full sm:w-auto">
                    <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                      <TabsTrigger value="pendentes" className="gap-2">
                        <Clock className="h-4 w-4" />
                        Pendentes
                      </TabsTrigger>
                      <TabsTrigger value="concluidas" className="gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Concluídas
                      </TabsTrigger>
                      <TabsTrigger value="todas" className="gap-2">
                        <Filter className="h-4 w-4" />
                        Todas
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardContent>
            </Card>

        {/* Lista de Entregas */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : entregasFiltradas.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Truck className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">
                {filterStatus === 'pendentes' ? 'Nenhuma entrega pendente' : 
                 filterStatus === 'concluidas' ? 'Nenhuma entrega concluída' : 
                 'Nenhuma entrega encontrada'}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? 'Tente alterar os filtros de busca' : 'As entregas aparecerão aqui quando forem registradas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entregasFiltradas.map((entrega) => {
              const isPendente = entrega.status !== 'Concluída';
              const isHoje = new Date(entrega.data_entrega).toDateString() === new Date().toDateString();
              const temAnexos = entrega.anexos && entrega.anexos.length > 0;
              const isRetiradaEntrega = entrega.tipo_entrega === 'retirada' || entrega.entregador === 'RETIRADA PELO CLIENTE';
              
              return (
                <Card 
                  key={entrega.id} 
                  className={`border-l-4 transition-all hover:shadow-md ${
                    !isPendente 
                      ? 'border-l-emerald-500 bg-emerald-500/5' 
                      : isRetiradaEntrega
                      ? 'border-l-amber-500 bg-amber-500/5'
                      : isHoje 
                      ? 'border-l-blue-500 bg-blue-500/5' 
                      : 'border-l-primary'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Info Principal */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge variant="outline" className="font-mono text-base px-3 py-1">
                            Romaneio #{String(entrega.numero_romaneio).padStart(4, '0')}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Pedido #{entrega.numero_pedido}
                          </Badge>
                          {isRetiradaEntrega && (
                            <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Retirada
                            </Badge>
                          )}
                          {isPendente ? (
                            isHoje ? (
                              <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">
                                <Calendar className="h-3 w-3 mr-1" />
                                Para Hoje
                              </Badge>
                            ) : (
                              <Badge className="bg-primary/20 text-primary border-primary/30">
                                <Clock className="h-3 w-3 mr-1" />
                                Pendente
                              </Badge>
                            )
                          ) : (
                            <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Concluída
                            </Badge>
                          )}
                          {temAnexos && (
                            <Badge variant="outline" className="text-xs">
                              <Image className="h-3 w-3 mr-1" />
                              {entrega.anexos.length} anexo(s)
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                          <div className="flex items-center gap-2 font-medium">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {entrega.cliente_nome}
                          </div>
                          {entrega.cliente_telefone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-4 w-4" />
                              {formatPhone(entrega.cliente_telefone)}
                            </div>
                          )}
                          {entrega.cliente_endereco && !isRetiradaEntrega && (
                            <div className="flex items-center gap-2 text-muted-foreground max-w-xs truncate">
                              <MapPin className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate">{entrega.cliente_endereco}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateDisplay(entrega.data_entrega)}
                          </div>
                          {!isRetiradaEntrega && (
                            <div className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {entrega.entregador}
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {entrega.itens.reduce((acc, item) => acc + item.quantidade_entregue, 0)} itens
                          </div>
                        </div>

                        {/* Lista de itens */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {entrega.itens.slice(0, 3).map((item, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs font-normal">
                              {item.quantidade_entregue}x {item.nome}
                            </Badge>
                          ))}
                          {entrega.itens.length > 3 && (
                            <Badge variant="outline" className="text-xs font-normal">
                              +{entrega.itens.length - 3} mais
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex items-center gap-2">
                        {!isPendente && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingEntrega(entrega)}
                            title="Ver Detalhes"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Detalhes
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => abrirPDF(entrega)}
                          title="Imprimir Romaneio"
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Imprimir
                        </Button>
                        
                        {isPendente && isRetiradaEntrega && (
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => setConfirmandoRetirada(entrega)}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Cliente Retirou
                          </Button>
                        )}
                        
                        {isPendente && !isRetiradaEntrega && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => abrirConclusao(entrega)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Concluir
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </TabsContent>

          {/* Tab de Pendentes da OS */}
          <TabsContent value="pendentes-os" className="space-y-4">
            {/* Filtros para Pendentes OS */}
            <Card>
              <CardContent className="p-4">
                <Tabs value={filterStatusOS} onValueChange={setFilterStatusOS} className="w-full">
                  <TabsList className="grid grid-cols-3 w-full max-w-md">
                    <TabsTrigger value="pendentes" className="gap-2">
                      <Clock className="h-4 w-4" />
                      Pendentes
                    </TabsTrigger>
                    <TabsTrigger value="concluidas" className="gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Concluídas
                    </TabsTrigger>
                    <TabsTrigger value="todas" className="gap-2">
                      <Filter className="h-4 w-4" />
                      Todas
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {loadingPendentes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : entregasPendentesOSFiltradas.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardList className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">
                    {filterStatusOS === 'pendentes' 
                      ? 'Nenhuma entrega pendente de OS'
                      : filterStatusOS === 'concluidas'
                        ? 'Nenhuma entrega concluída de OS'
                        : 'Nenhuma entrega de OS'}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {filterStatusOS === 'pendentes' 
                      ? 'Quando uma Ordem de Serviço for finalizada, os itens aparecerão aqui para gerar o romaneio'
                      : 'Altere o filtro para ver outras entregas'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {entregasPendentesOSFiltradas.map((pendente) => {
                  const totalItens = pendente.itens?.length || 0;
                  const itensPendentes = pendente.itens?.filter(i => i.status !== 'entregue').length || 0;
                  const isEntregue = pendente.status === 'entregue';
                  
                  return (
                    <Card 
                      key={pendente.id} 
                      className={`border-l-4 hover:shadow-md transition-all ${
                        isEntregue ? 'border-l-green-500 bg-green-50/30' : 'border-l-blue-500'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              <Badge className={`font-mono ${
                                isEntregue 
                                  ? 'bg-green-500/20 text-green-600 border-green-500/30'
                                  : 'bg-blue-500/20 text-blue-600 border-blue-500/30'
                              }`}>
                                <ClipboardList className="h-3 w-3 mr-1" />
                                OS-{String(pendente.numero_os).padStart(4, '0')}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {pendente.numero_pedido}
                              </Badge>
                              <Badge className={isEntregue 
                                ? 'bg-green-500/20 text-green-600 border-green-500/30'
                                : 'bg-amber-500/20 text-amber-600 border-amber-500/30'
                              }>
                                {isEntregue ? (
                                  <><CheckCircle className="h-3 w-3 mr-1" /> Entrega Concluída</>
                                ) : (
                                  <><Clock className="h-3 w-3 mr-1" /> Aguardando Entrega</>
                                )}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-2 font-medium">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {pendente.cliente_nome}
                            </div>

                            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                OS finalizada: {formatDateDisplay(pendente.data_finalizacao_os)}
                              </div>
                              <div className="flex items-center gap-1">
                                <Package className="h-3 w-3" />
                                {isEntregue 
                                  ? `${totalItens} itens entregues` 
                                  : `${itensPendentes} de ${totalItens} itens pendentes`
                                }
                              </div>
                            </div>

                            {/* Lista de itens */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {pendente.itens?.slice(0, 4).map((item, idx) => (
                                <Badge 
                                  key={idx} 
                                  variant="outline" 
                                  className={`text-xs font-normal ${
                                    item.status === 'entregue' 
                                      ? 'bg-green-500/10 text-green-600 line-through' 
                                      : ''
                                  }`}
                                >
                                  {item.quantidade_pedida}x {item.nome}
                                </Badge>
                              ))}
                              {pendente.itens?.length > 4 && (
                                <Badge variant="outline" className="text-xs font-normal">
                                  +{pendente.itens.length - 4} mais
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Ações */}
                          <div className="flex items-center gap-2">
                            {/* Botão excluir para OS sem pedido (estoque interno) */}
                            {!pendente.pedido_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => excluirEntregaPendente(pendente.id)}
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                title="Excluir (OS de estoque interno)"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Excluir
                              </Button>
                            )}
                            {!isEntregue && (
                              <Button
                                size="sm"
                                onClick={() => abrirGerarEntrega(pendente)}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <Truck className="h-4 w-4 mr-1" />
                                Gerar Entrega
                              </Button>
                            )}
                            {isEntregue && (
                              <Badge className="bg-green-500 text-white">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Concluída
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Modal de Confirmação com Upload */}
        <Dialog open={!!selectedEntrega} onOpenChange={(open) => !open && setSelectedEntrega(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                Confirmar Entrega
              </DialogTitle>
              <DialogDescription>
                Confirme que a entrega foi realizada e anexe os comprovantes
              </DialogDescription>
            </DialogHeader>

            {selectedEntrega && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Romaneio</span>
                    <Badge variant="outline" className="font-mono">
                      #{String(selectedEntrega.numero_romaneio).padStart(4, '0')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cliente</span>
                    <span className="font-medium text-sm">{selectedEntrega.cliente_nome}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Entregador</span>
                    <span className="text-sm">{selectedEntrega.entregador}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Itens</span>
                    <span className="text-sm">{selectedEntrega.itens.reduce((acc, i) => acc + i.quantidade_entregue, 0)} produtos</span>
                  </div>
                </div>

                {/* Upload de Arquivos */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Anexar Comprovantes (opcional)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Romaneio assinado, fotos dos produtos entregues, etc.
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Selecionar Arquivos
                  </Button>
                  
                  {/* Lista de arquivos */}
                  {arquivos.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {arquivos.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded border">
                          <div className="flex items-center gap-2">
                            {file.type.startsWith('image/') ? (
                              <Image className="h-4 w-4 text-blue-500" />
                            ) : (
                              <File className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive"
                            onClick={() => removerArquivo(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Observações da Conclusão */}
                <div className="space-y-2">
                  <Label htmlFor="obs_conclusao">Observações da Entrega (opcional)</Label>
                  <Textarea
                    id="obs_conclusao"
                    placeholder="Ex: Cliente satisfeito, entregue no portão..."
                    value={observacoesConclusao}
                    onChange={(e) => setObservacoesConclusao(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />
                    <p className="text-xs text-emerald-700">
                      Ao confirmar, o status do pedido será atualizado automaticamente 
                      e esta entrega será marcada como concluída.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setSelectedEntrega(null)} disabled={confirmando}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={concluirEntrega} 
                    disabled={confirmando}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {confirmando ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Confirmar Entrega
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes da Entrega Concluída */}
        <Dialog open={!!viewingEntrega} onOpenChange={(open) => !open && setViewingEntrega(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                Detalhes da Entrega
              </DialogTitle>
            </DialogHeader>

            {viewingEntrega && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Romaneio</span>
                    <Badge variant="outline" className="font-mono">
                      #{String(viewingEntrega.numero_romaneio).padStart(4, '0')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cliente</span>
                    <span className="font-medium text-sm">{viewingEntrega.cliente_nome}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Entregador</span>
                    <span className="text-sm">{viewingEntrega.entregador}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Data Entrega</span>
                    <span className="text-sm">{formatDateDisplay(viewingEntrega.data_entrega)}</span>
                  </div>
                  {viewingEntrega.data_conclusao && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Concluída em</span>
                      <span className="text-sm">{new Date(viewingEntrega.data_conclusao).toLocaleString('pt-BR')}</span>
                    </div>
                  )}
                  {viewingEntrega.concluido_por && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Concluída por</span>
                      <span className="text-sm">{viewingEntrega.concluido_por}</span>
                    </div>
                  )}
                </div>

                {/* Itens */}
                <div className="space-y-2">
                  <Label>Itens Entregues</Label>
                  <div className="space-y-1">
                    {viewingEntrega.itens.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <span className="text-sm">{item.nome}</span>
                        <Badge variant="secondary">{item.quantidade_entregue}x</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Observações */}
                {viewingEntrega.observacoes_conclusao && (
                  <div className="space-y-2">
                    <Label>Observações da Entrega</Label>
                    <p className="text-sm p-2 bg-muted/30 rounded">
                      {viewingEntrega.observacoes_conclusao}
                    </p>
                  </div>
                )}

                {/* Anexos */}
                {viewingEntrega.anexos && viewingEntrega.anexos.length > 0 && (
                  <div className="space-y-2">
                    <Label>Comprovantes Anexados</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {viewingEntrega.anexos.map((anexo, idx) => (
                        <div 
                          key={idx} 
                          className="p-2 bg-muted/30 rounded border cursor-pointer hover:bg-muted/50"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${anexo.url}`, '_blank')}
                        >
                          <div className="flex items-center gap-2">
                            {anexo.tipo === 'foto' ? (
                              <Image className="h-4 w-4 text-blue-500" />
                            ) : (
                              <File className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-xs truncate">{anexo.nome}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => abrirPDF(viewingEntrega)}>
                    <Printer className="h-4 w-4 mr-2" />
                    Reimprimir Romaneio
                  </Button>
                  <Button onClick={() => setViewingEntrega(null)}>
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Gerar Entrega a partir de OS Pendente */}
        <Dialog open={!!selectedPendente} onOpenChange={(open) => !open && setSelectedPendente(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-blue-600" />
                Gerar Entrega
              </DialogTitle>
              <DialogDescription>
                {selectedPendente && (
                  <>
                    OS-{String(selectedPendente.numero_os).padStart(4, '0')} • {selectedPendente.cliente_nome}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {selectedPendente && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Pedido</span>
                    <Badge variant="outline">{selectedPendente.numero_pedido}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cliente</span>
                    <span className="font-medium text-sm">{selectedPendente.cliente_nome}</span>
                  </div>
                </div>

                {/* Itens para entrega */}
                <div className="space-y-2">
                  <Label>Selecione os itens e quantidades a entregar:</Label>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {itensSelecionadosEntrega.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            Pedido: {item.quantidade_pedida} | Já entregue: {item.quantidade_entregue || 0}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Qtd:</Label>
                          <Input
                            type="number"
                            min={0}
                            max={item.quantidade_pedida - (item.quantidade_entregue || 0)}
                            value={item.quantidade_a_entregar}
                            onChange={(e) => updateQuantidadeEntrega(idx, parseInt(e.target.value) || 0)}
                            className="w-20 h-8 text-center"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Opção Retirada pelo Cliente */}
                <div className="flex items-center space-x-2 p-3 border rounded-lg bg-amber-500/5 border-amber-500/30">
                  <Checkbox 
                    id="retirada" 
                    checked={isRetirada}
                    onCheckedChange={setIsRetirada}
                  />
                  <Label htmlFor="retirada" className="flex items-center gap-2 cursor-pointer">
                    <UserCheck className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-amber-700 dark:text-amber-400">Cliente vai retirar</span>
                  </Label>
                </div>

                {/* Campo do Entregador - só mostra se não for retirada */}
                {!isRetirada && (
                  <div className="space-y-2">
                    <Label htmlFor="entregador" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Nome do Entregador <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="entregador"
                      placeholder="Digite o nome do entregador..."
                      value={entregadorNome}
                      onChange={(e) => setEntregadorNome(e.target.value)}
                      className="w-full"
                    />
                  </div>
                )}

                <div className={`p-3 rounded-lg border ${isRetirada ? 'bg-amber-500/10 border-amber-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
                  <div className="flex items-start gap-2">
                    {isRetirada ? (
                      <>
                        <UserCheck className="h-4 w-4 text-amber-600 mt-0.5" />
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          Um romaneio de retirada será criado. O cliente deverá assinar o documento 
                          ao retirar os produtos no local.
                        </p>
                      </>
                    ) : (
                      <>
                        <Truck className="h-4 w-4 text-blue-600 mt-0.5" />
                        <p className="text-xs text-blue-700 dark:text-blue-400">
                          Uma nova entrega/romaneio será criada com os itens selecionados. 
                          Você poderá imprimir o romaneio e registrar a conclusão depois.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setSelectedPendente(null); setIsRetirada(false); }} disabled={gerandoEntrega}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleGerarEntrega} 
                    disabled={gerandoEntrega || itensSelecionadosEntrega.every(i => i.quantidade_a_entregar === 0) || (!isRetirada && !entregadorNome.trim())}
                    className={isRetirada ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {gerandoEntrega ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : isRetirada ? (
                      <UserCheck className="h-4 w-4 mr-2" />
                    ) : (
                      <Truck className="h-4 w-4 mr-2" />
                    )}
                    {isRetirada ? 'Gerar Romaneio de Retirada' : 'Gerar Romaneio de Entrega'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmação de Retirada */}
        <Dialog open={!!confirmandoRetirada} onOpenChange={(open) => !open && setConfirmandoRetirada(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-amber-500" />
                Confirmar Retirada
              </DialogTitle>
              <DialogDescription>
                Confirme que o cliente retirou os produtos do romaneio.
              </DialogDescription>
            </DialogHeader>
            
            {confirmandoRetirada && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Romaneio:</span>
                    <span className="font-medium">#{String(confirmandoRetirada.numero_romaneio).padStart(4, '0')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Pedido:</span>
                    <span className="font-medium">#{confirmandoRetirada.numero_pedido}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Cliente:</span>
                    <span className="font-medium">{confirmandoRetirada.cliente_nome}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Itens:</span>
                    <span className="font-medium">
                      {confirmandoRetirada.itens?.reduce((acc, item) => acc + item.quantidade_entregue, 0)} produtos
                    </span>
                  </div>
                </div>
                
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Ao confirmar, o romaneio será marcado como concluído e os itens serão considerados entregues ao cliente.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmandoRetirada(null)} disabled={confirmando}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmarRetirada} 
                disabled={confirmando}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {confirmando ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UserCheck className="h-4 w-4 mr-2" />
                )}
                Confirmar Retirada
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
